import { Button } from "@loot-os/ui/components/button";
import { Calendar } from "@loot-os/ui/components/calendar";
import { Field, FieldLabel } from "@loot-os/ui/components/field";
import { Input } from "@loot-os/ui/components/input";
import { Popover, PopoverContent, PopoverTrigger } from "@loot-os/ui/components/popover";
import { CalendarIcon } from "lucide-react";
import { useState } from "react";

import { formatShortDate } from "../lib/format";
import type { LocalSchedule } from "../lib/localSchedule";

/**
 * A moment as two local fields: the day, from a calendar, and the time. `id` prefixes both
 * inputs' ids; `notBefore` greys out the calendar's days before its own.
 */
export function DayTimeFields({
  id,
  value,
  disabled,
  notBefore,
  onChange,
}: {
  id: string;
  value: LocalSchedule;
  disabled: boolean;
  notBefore?: Date;
  onChange: (value: LocalSchedule) => void;
}) {
  const [picking, setPicking] = useState(false);
  return (
    <div className="flex flex-wrap items-end gap-3">
      <Field className="w-auto">
        <FieldLabel htmlFor={`${id}-day`}>Day</FieldLabel>
        <Popover open={picking} onOpenChange={setPicking}>
          <PopoverTrigger
            render={
              <Button
                id={`${id}-day`}
                type="button"
                variant="outline"
                disabled={disabled}
                className="w-44 justify-start px-2.5 font-normal"
              />
            }
          >
            <CalendarIcon data-icon="inline-start" className="text-muted-foreground" />
            {formatShortDate(value.day)}
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              required
              defaultMonth={value.day}
              selected={value.day}
              disabled={
                notBefore
                  ? {
                      before: new Date(
                        notBefore.getFullYear(),
                        notBefore.getMonth(),
                        notBefore.getDate(),
                      ),
                    }
                  : undefined
              }
              onSelect={(day) => {
                onChange({ ...value, day });
                setPicking(false);
              }}
            />
          </PopoverContent>
        </Popover>
      </Field>
      <Field className="w-auto">
        <FieldLabel htmlFor={`${id}-time`}>Time</FieldLabel>
        <Input
          id={`${id}-time`}
          type="time"
          className="w-32"
          value={value.time}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, time: e.target.value })}
        />
      </Field>
    </div>
  );
}
